import React, { createContext, useContext, useReducer, useEffect } from 'react';
import cartApi from '../services/cartApi';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext();

// Cart action types
const CART_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_CART: 'SET_CART',
  SET_CART_SUMMARY: 'SET_CART_SUMMARY',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  CLEAR_CART: 'CLEAR_CART',
  SET_ERROR: 'SET_ERROR'
};

// Initial cart state
const initialState = {
  cart: null,
  summary: { itemCount: 0, totalPrice: 0 },
  loading: false,
  error: null
};

// Cart reducer function
function cartReducer(state, action) {
  switch (action.type) {
    case CART_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case CART_ACTIONS.SET_CART:
      return {
        ...state,
        cart: action.payload,
        summary: {
          itemCount: action.payload?.items?.length || 0,
          totalPrice: action.payload?.totalPrice || 0
        },
        loading: false,
        error: null
      };
    
    case CART_ACTIONS.SET_CART_SUMMARY:
      return { ...state, summary: action.payload };
    
    case CART_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        cart: null,
        summary: { itemCount: 0, totalPrice: 0 }
      };
    
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user, isAuthenticated } = useAuth();

  // Load cart on authentication
  useEffect(() => {
    if (isAuthenticated && user) {
      loadCart();
    } else {
      dispatch({ type: CART_ACTIONS.CLEAR_CART });
    }
  }, [isAuthenticated, user]);

  // Cart operations
  const loadCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const cart = await cartApi.getCart();
      dispatch({ type: CART_ACTIONS.SET_CART, payload: cart });
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const loadCartSummary = async () => {
    try {
      const summary = await cartApi.getCartSummary();
      dispatch({ type: CART_ACTIONS.SET_CART_SUMMARY, payload: summary });
    } catch (error) {
      console.error('Failed to load cart summary:', error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const updatedCart = await cartApi.addToCart(productId, quantity);
      dispatch({ type: CART_ACTIONS.SET_CART, payload: updatedCart });
      toast.success('Item added to cart');
      return true;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      toast.error(error.message || 'Failed to add item to cart');
      return false;
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const updatedCart = await cartApi.updateCartItem(itemId, quantity);
      dispatch({ type: CART_ACTIONS.SET_CART, payload: updatedCart });
      toast.success('Cart updated');
      return true;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      toast.error(error.message || 'Failed to update cart');
      return false;
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      const updatedCart = await cartApi.removeFromCart(itemId);
      dispatch({ type: CART_ACTIONS.SET_CART, payload: updatedCart });
      toast.success('Item removed from cart');
      return true;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      toast.error(error.message || 'Failed to remove item');
      return false;
    }
  };

  const clearCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });
      await cartApi.clearCart();
      dispatch({ type: CART_ACTIONS.CLEAR_CART });
      toast.success('Cart cleared');
      return true;
    } catch (error) {
      dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
      toast.error(error.message || 'Failed to clear cart');
      return false;
    }
  };

  const value = {
    // State
    cart: state.cart,
    summary: state.summary,
    loading: state.loading,
    error: state.error,
    
    // Actions
    loadCart,
    loadCartSummary,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

// Custom hook to use cart context
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}